/**
 * BREWIE STICKER BOOK
 * Handles: collect, persist (localStorage + metafield), rewards, animations
 */

const STORAGE_KEY = 'brewie_stickers';
const METAFIELD_NAMESPACE = 'custom';
const METAFIELD_KEY = 'sticker_collection';

// ─── Rewards config ────────────────────────────────────────────────────────
// Override codes via theme settings: window.BREWIE.stickerRewards
const DEFAULT_REWARDS = [
  { type: 'total', threshold: 5,  code: 'STICKER5',   label: '10% off your order',    sublabel: 'Collect 5 stickers' },
  { type: 'total', threshold: 10, code: 'STICKER10',  label: 'Free shipping',          sublabel: 'Collect 10 stickers' },
  { type: 'set',   set: 'summer-collection', code: 'SUMMER25', label: '25% off summer collection', sublabel: 'Complete the Summer set' },
];

function getRewards() {
  const overrides = window.BREWIE?.stickerRewards || {};
  return DEFAULT_REWARDS.map(r => {
    let code = r.code;
    if (r.code === 'STICKER5' && overrides.five) code = overrides.five;
    if (r.code === 'STICKER10' && overrides.ten) code = overrides.ten;
    if (r.code === 'SUMMER25' && overrides.set) code = overrides.set;
    return { ...r, code };
  });
}

const REWARDS = getRewards();

function escapeHTML(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// ─── State ─────────────────────────────────────────────────────────────────
let state = { stickers: {}, redeemedCodes: [] };

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      state = JSON.parse(raw);
      if (!state || typeof state.stickers !== 'object' || !Array.isArray(state.redeemedCodes)) {
        state = { stickers: {}, redeemedCodes: [] };
      }
    }
  } catch (e) {
    state = { stickers: {}, redeemedCodes: [] };
  }
}

function saveState() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  syncToMetafield();
}

// ─── Metafield sync ────────────────────────────────────────────────────────
async function syncToMetafield() {
  // Only sync if customer is logged in (Shopify sets window.Shopify.customerId)
  const customerId = window.Shopify?.customerId;
  if (!customerId) return;

  try {
    await fetch('/api/2024-01/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.BREWIE?.storefrontToken || window.BREWIE_STOREFRONT_TOKEN || '',
      },
      body: JSON.stringify({
        query: `
          mutation customerUpdate($customerAccessToken: String!, $customer: CustomerUpdateInput!) {
            customerUpdate(customerAccessToken: $customerAccessToken, customer: $customer) {
              customer { id }
              userErrors { field message }
            }
          }
        `,
        variables: {
          customerAccessToken: getCustomerToken(),
          customer: {
            metafields: [{
              namespace: METAFIELD_NAMESPACE,
              key: METAFIELD_KEY,
              value: JSON.stringify(state),
              type: 'json',
            }]
          }
        }
      })
    });
  } catch (e) {
    // silent fail — localStorage is still source of truth
  }
}

function getCustomerToken() {
  // Shopify stores customer token in cookie _shopify_customer_token
  const match = document.cookie.match(/_shopify_customer_token=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : '';
}

// ─── Collection logic ──────────────────────────────────────────────────────
function isCollected(id) {
  return !!state.stickers[id]?.collected;
}

function collect(id, name, img, set) {
  if (isCollected(id)) return false;

  state.stickers[id] = {
    id, name, img, set: set || null,
    collected: true,
    collectedAt: new Date().toISOString(),
  };
  saveState();
  checkRewards();
  updateProgress();
  return true;
}

function getCollectedCount() {
  return Object.values(state.stickers).filter(s => s.collected).length;
}

function getSetProgress(setId) {
  const all = document.querySelectorAll(`[data-sticker-set="${setId}"]`);
  const total = all.length;
  const collected = Array.from(all).filter(el => isCollected(el.dataset.stickerId)).length;
  return { collected, total };
}

// ─── Rewards ───────────────────────────────────────────────────────────────
function checkRewards() {
  const total = getCollectedCount();

  REWARDS.forEach(reward => {
    if (state.redeemedCodes?.includes(reward.code)) return;

    let unlocked = false;
    if (reward.type === 'total' && total >= reward.threshold) {
      unlocked = true;
    }
    if (reward.type === 'set') {
      const setStickers = Object.values(state.stickers).filter(
        s => s.collected && s.set === reward.set
      );
      // check all stickers of this set are collected
      const allSetEmbeds = document.querySelectorAll(`[data-sticker-set="${reward.set}"]`);
      if (allSetEmbeds.length > 0 && setStickers.length >= allSetEmbeds.length) {
        unlocked = true;
      }
    }

    if (unlocked) {
      if (!state.redeemedCodes) state.redeemedCodes = [];
      state.redeemedCodes.push(reward.code);
      saveState();
      showRewardNotification(reward);
    }
  });
}

function showRewardNotification(reward) {
  const el = document.createElement('div');
  el.style.cssText = `
    position:fixed;top:24px;left:50%;transform:translateX(-50%);
    background:#2b1a0e;color:#f5e6c8;padding:14px 20px;border-radius:10px;
    font-size:14px;font-weight:600;z-index:9999;box-shadow:0 4px 20px rgba(0,0,0,0.4);
    border:1px solid #ffd700;text-align:center;
  `;
  el.innerHTML = `🎉 reward unlocked: <strong>${reward.label}</strong><br><span style="font-size:11px;opacity:0.7">open your sticker book to claim it</span>`;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 4000);
}

// ─── Collect animation ─────────────────────────────────────────────────────
function animateCollect(embedEl, imgSrc) {
  const trigger = document.querySelector('.sticker-book-trigger');
  if (!trigger) return;

  const fromRect = embedEl.getBoundingClientRect();
  const toRect = trigger.getBoundingClientRect();

  const fly = document.createElement('img');
  fly.src = imgSrc;
  fly.className = 'sticker-fly';
  fly.style.left = `${fromRect.left + fromRect.width / 2 - 28}px`;
  fly.style.top = `${fromRect.top + fromRect.height / 2 - 28}px`;
  document.body.appendChild(fly);

  requestAnimationFrame(() => {
    fly.style.transition = 'left 0.5s cubic-bezier(0.4, 0, 0.2, 1), top 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.5s, transform 0.5s';
    fly.style.left = `${toRect.left + toRect.width / 2 - 28}px`;
    fly.style.top = `${toRect.top + toRect.height / 2 - 28}px`;
    fly.style.opacity = '0';
    fly.style.transform = 'scale(0.4)';
  });

  setTimeout(() => {
    fly.remove();
    bounceTrigger();
    updateBadge();
    updateProgress();
  }, 520);
}

function bounceTrigger() {
  const trigger = document.querySelector('.sticker-book-trigger');
  if (!trigger) return;
  trigger.classList.remove('bounce');
  void trigger.offsetWidth; // reflow
  trigger.classList.add('bounce');
  setTimeout(() => trigger.classList.remove('bounce'), 400);
}

// ─── Trigger button ────────────────────────────────────────────────────────
function updateBadge() {
  const badge = document.querySelector('.sticker-book-trigger__badge');
  if (!badge) return;
  const count = getCollectedCount();
  badge.textContent = count;
  if (count > 0) {
    badge.classList.add('is-visible');
  }
}

function updateProgress() {
  const bar = document.querySelector('[data-sticker-progress-bar]');
  const text = document.querySelector('[data-sticker-progress-text]');
  if (!bar || !text) return;

  const totalEmbeds = document.querySelectorAll('[data-sticker-id]').length;
  const collected = getCollectedCount();
  const pct = totalEmbeds > 0 ? Math.round((collected / totalEmbeds) * 100) : 0;
  bar.style.width = pct + '%';
  text.textContent = `${collected} of ${totalEmbeds} stickers collected`;
}

function initTrigger() {
  const trigger = document.querySelector('.sticker-book-trigger');
  if (trigger) {
    trigger.classList.add('is-visible');
    updateBadge();
    trigger.addEventListener('click', openBook);
  }
  document.querySelectorAll('[data-sticker-book-open]').forEach(btn => {
    btn.addEventListener('click', openBook);
  });
}

// ─── Modal ─────────────────────────────────────────────────────────────────
function openBook() {
  renderBook();
  document.querySelector('.sticker-book-overlay')?.classList.add('is-open');
  document.querySelector('.sticker-book-modal')?.classList.add('is-open');
  document.body.style.overflow = 'hidden';
}

function closeBook() {
  document.querySelector('.sticker-book-overlay')?.classList.remove('is-open');
  document.querySelector('.sticker-book-modal')?.classList.remove('is-open');
  document.body.style.overflow = '';
}

function renderBook() {
  renderGrid();
  renderSets();
  renderRewards();
}

function renderGrid() {
  const grid = document.querySelector('.sticker-book-grid');
  if (!grid) return;

  // Gather all stickers registered on this page + from state
  const allIds = new Set([
    ...Object.keys(state.stickers),
    ...Array.from(document.querySelectorAll('[data-sticker-id]')).map(el => el.dataset.stickerId)
  ]);

  grid.innerHTML = '';
  allIds.forEach(id => {
    const s = state.stickers[id];
    const embedEl = document.querySelector(`[data-sticker-id="${id}"]`);
    const name = s?.name || embedEl?.dataset.stickerName || id;
    const img = s?.img || embedEl?.dataset.stickerImg || '';
    const collected = !!s?.collected;

    const item = document.createElement('div');
    item.className = `sticker-book-item${collected ? '' : ' sticker-book-item--locked'}`;
    item.innerHTML = `
      <div class="sticker-book-item__img-wrap">
        <img class="sticker-book-item__img" src="${escapeHTML(img)}" alt="${escapeHTML(name)}" loading="lazy">
        ${collected ? '<div class="sticker-book-item__check">✓</div>' : ''}
      </div>
      <div class="sticker-book-item__name">${collected ? escapeHTML(name) : '???'}</div>
    `;
    grid.appendChild(item);
  });
}

function renderSets() {
  const container = document.querySelector('.sticker-book-sets');
  if (!container) return;

  // Build set totals from DOM embeds (source of truth for what stickers exist)
  const sets = {};
  document.querySelectorAll('[data-sticker-set]').forEach(el => {
    const setId = el.dataset.stickerSet;
    if (!sets[setId]) sets[setId] = { collected: 0, total: 0 };
    sets[setId].total++;
    if (isCollected(el.dataset.stickerId)) sets[setId].collected++;
  });

  // Also include sets from state that may not be on this page
  Object.values(state.stickers).forEach(s => {
    if (s.set && s.collected && !sets[s.set]) {
      sets[s.set] = { collected: 1, total: 1 };
    }
  });

  if (Object.keys(sets).length === 0) {
    container.innerHTML = '';
    return;
  }

  container.innerHTML = Object.entries(sets).map(([setId, { collected, total }]) => {
    const pct = total > 0 ? Math.round((collected / total) * 100) : 0;
    return `
      <div class="sticker-book-set">
        <div class="sticker-book-set__label">
          <span>${escapeHTML(setId.replace(/-/g, ' '))}</span>
          <span>${collected}/${total}</span>
        </div>
        <div class="sticker-book-set__bar">
          <div class="sticker-book-set__fill" style="width:${pct}%"></div>
        </div>
      </div>
    `;
  }).join('');
}

function renderRewards() {
  const container = document.querySelector('.sticker-book-rewards-list');
  if (!container) return;

  const total = getCollectedCount();

  container.innerHTML = REWARDS.map(reward => {
    const unlocked = state.redeemedCodes?.includes(reward.code);
    const progress = reward.type === 'total'
      ? `${Math.min(total, reward.threshold)}/${reward.threshold} stickers`
      : reward.sublabel;

    return `
      <div class="sticker-book-reward${unlocked ? '' : ' sticker-book-reward--locked'}">
        <div>
          <div class="sticker-book-reward__label">${reward.label}</div>
          <div class="sticker-book-reward__sublabel">${progress}</div>
        </div>
        ${unlocked
          ? `<button class="sticker-book-reward__copy" data-code="${reward.code}">copy code</button>`
          : `<span style="color:rgba(245,230,200,0.3);font-size:12px">locked</span>`
        }
      </div>
    `;
  }).join('');

  container.querySelectorAll('.sticker-book-reward__copy').forEach(btn => {
    btn.addEventListener('click', () => {
      navigator.clipboard.writeText(btn.dataset.code).then(() => {
        btn.textContent = 'copied!';
        setTimeout(() => btn.textContent = 'copy code', 1500);
      });
    });
  });
}

// ─── Sticker embeds ────────────────────────────────────────────────────────
function initEmbeds() {
    document.querySelectorAll('[data-sticker-id]').forEach(embedEl => {
      const rotation = (Math.random() * 16 - 8).toFixed(1);
      const offset = Math.floor(Math.random() * 80);
      embedEl.style.display = 'block';
      embedEl.style.width = 'fit-content';
      embedEl.style.marginLeft = offset + '%';
      embedEl.style.transform = `rotate(${rotation}deg)`;
      embedEl.style.setProperty('--base-transform',
  `rotate(${rotation}deg)`);
    const id = embedEl.dataset.stickerId;
    const name = embedEl.dataset.stickerName;
    const img = embedEl.dataset.stickerImg;
    const set = embedEl.dataset.stickerSet || null;

    // Apply collected state
    if (isCollected(id)) {
      embedEl.classList.add('sticker-embed--collected');
    }

embedEl.addEventListener('click', () => {
        const wasNew = collect(id, name, img, set);
        if (wasNew) {
          embedEl.classList.add('sticker-embed--collected');
          const imgEl = embedEl.querySelector('.sticker-embed__img');
          animateCollect(embedEl, imgEl?.src || '');
        }
      });

      embedEl.addEventListener('mouseenter', () => {
        const base = embedEl.style.getPropertyValue('--base-transform');
        embedEl.style.transform = base + ' scale(1.08)';
      });
      embedEl.addEventListener('mouseleave', () => {
        embedEl.style.transform =
  embedEl.style.getPropertyValue('--base-transform');
      });
  });
}

// ─── Modal close handlers ──────────────────────────────────────────────────
function initModal() {
  document.querySelector('.sticker-book-overlay')?.addEventListener('click', closeBook);
  document.querySelector('.sticker-book-modal__close')?.addEventListener('click', closeBook);
}

// ─── Boot ──────────────────────────────────────────────────────────────────
document.addEventListener('DOMContentLoaded', () => {
  loadState();
  initTrigger();
  initEmbeds();
  initModal();
  updateProgress();
});
